import type { TenantVerticalLiteral } from "@eduviaos/shared";

/**
 * SC Foundation (F0) — the single source of truth for vertical-specific
 * vocabulary and which school-shaped concepts a vertical actually has.
 *
 * Every SC-facing surface (People/Learners roster, dashboard, nav
 * "in-progress" screens, onboarding copy) reads from this profile
 * instead of scattering `vertical === "study_centre"` checks. That keeps
 * de-schooling consistent and lets school / coaching keep their own
 * shape without regressions.
 *
 * The BE `capability/data/vertical-defaults.yml` is the runtime source of
 * truth for behaviour (stages, fee plans, attendance mode). This FE
 * profile is the *presentation* mirror — copy, columns, KPI ordering.
 */

/** Canonical roster column keys the People page understands. */
export type PersonColumnKey =
  | "name"
  | "email"
  | "phone"
  | "grade"
  | "section"
  | "roll"
  | "parent"
  | "shift"
  | "plan"
  | "status"
  | "joined";

/** School-shaped concept flags — `false` hides the concept entirely. */
export interface VerticalConceptFlags {
  grades: boolean;
  sections: boolean;
  parents: boolean;
  classTeachers: boolean;
  subjects: boolean;
  periods: boolean;
  exams: boolean;
  reportCards: boolean;
  /**
   * Phase SC-4c — whether learners of this vertical can be given a login
   * to the learner portal. Mirrors the BE's `require_vertical` gate on
   * `POST /me/learner-invites`: only the study-centre learner app is
   * shipped, so offering "Invite to portal" anywhere else would send a
   * real child to a screen full of prototype fixtures.
   */
  learnerPortal: boolean;
  /**
   * Phase SC-4e — whether the IA sidebar should surface the H5
   * **Assessments** authoring workspace (entrance-test templates bound
   * to admission stages). Study centres run a light enquiry funnel and
   * learner self-tests live in the portal Learn tab — not here.
   */
  admissionAssessments: boolean;
}

/**
 * Copy for the app-shell chrome — the bits an admin sees on *every*
 * page regardless of which module they're in.
 *
 * This is where de-schooling is most visible and was longest overdue:
 * the roster page already said "Learners" while the sidebar row that
 * led to it still said "People" and the search box still offered to
 * find "students, classes, fees".
 */
export interface ChromeCopy {
  /** Topbar search placeholder. */
  searchPlaceholder: string;
  /**
   * Sidebar label overrides keyed by module key. Anything absent
   * falls back to the neutral `MODULE_CATALOG` label, so a vertical
   * only declares the rows where its vocabulary actually differs.
   */
  navLabels: Partial<Record<string, string>>;
}

/** Per-module copy override shown on the "in-progress" screen. */
export interface ModuleCopy {
  /** One-line "what this does for your <institution>". */
  blurb: string;
  /** Optional teaser for the AI assistance this module will bring. */
  aiTeaser?: string;
}

/** Singular + plural pair for a client-facing noun. */
export interface NounPair {
  one: string;
  many: string;
}

/**
 * Phase SC-22 — every client-facing noun a screen may need, per
 * vertical. Screens read these instead of hardcoding "Student",
 * "Staff", "Roll #" (Iron Rule #20). Stored role values and API field
 * names never change; only these labels do.
 */
export interface VerticalTerms {
  /** The people who learn here. */
  learner: NounPair;
  /** The people who work here, as a group. */
  team: NounPair;
  /** A classroom-facing role (`teacher`). */
  mentor: NounPair;
  /** The adult responsible for a minor. */
  guardian: NounPair;
  /** Label for `roll_number`. */
  enrolmentId: string;
  /** Label for `admission_number`. */
  membershipNo: string;
  /** Label for `admission_date`. */
  joiningDate: string;
  /** The intake funnel (enquiry → join). */
  admission: NounPair;
  /** Whoever works an enquiry. */
  counsellor: NounPair;
  grade: string;
  section: string;
}

export interface VerticalProfile {
  vertical: TenantVerticalLiteral;
  /** Human label for the institution kind. */
  institutionNoun: string;
  /** Client-facing nouns (Phase SC-22). */
  terms: VerticalTerms;
  /** The people who learn / study here. */
  personNoun: { one: string; many: string };
  /** Staff framing (SC has no "teachers"). */
  staffNoun: { one: string; many: string };
  has: VerticalConceptFlags;
  /** App-shell copy (topbar search, sidebar labels). */
  chrome: ChromeCopy;
  /** Roster columns in display order. */
  personColumns: readonly PersonColumnKey[];
  /** Dashboard KPI keys in display order. */
  dashboardKpis: readonly string[];
  /** Vertical-specific in-progress copy, keyed by module key. */
  moduleCopy: Partial<Record<string, ModuleCopy>>;
}

const STUDY_CENTRE: VerticalProfile = {
  vertical: "study_centre",
  institutionNoun: "Study centre",
  terms: {
    learner: { one: "Learner", many: "Learners" },
    team: { one: "Team member", many: "Team" },
    mentor: { one: "Mentor", many: "Mentors" },
    guardian: { one: "Guardian", many: "Guardians" },
    enrolmentId: "Learner ID",
    membershipNo: "Membership no.",
    joiningDate: "Joining date",
    admission: { one: "Enquiry", many: "Enquiries" },
    counsellor: { one: "Team member", many: "Team" },
    grade: "Grade",
    section: "Section",
  },
  personNoun: { one: "Learner", many: "Learners" },
  staffNoun: { one: "Team member", many: "Team" },
  has: {
    grades: false,
    sections: false,
    parents: false,
    classTeachers: false,
    subjects: false,
    periods: false,
    exams: false,
    reportCards: false,
    learnerPortal: true,
    admissionAssessments: false,
  },
  chrome: {
    // A study centre sells seats and hours, so those are the nouns an
    // owner searches by. "Classes" would be a dead query here.
    searchPlaceholder: "Search learners, seats, fees…",
    navLabels: { people: "Learners" },
  },
  personColumns: ["name", "phone", "shift", "plan", "status", "joined"],
  dashboardKpis: [
    "active_learners",
    "seat_utilization",
    "daily_check_ins",
    "revenue",
    "net_profit",
  ],
  moduleCopy: {
    people: {
      blurb:
        "Your learners — no grades, sections, or parents to manage. Your team has its own page.",
    },
    admissions: {
      blurb:
        "Walk-in enquiries and direct admissions — a light two-stage funnel, not a school application process.",
    },
    crm: {
      blurb: "Track walk-in enquiries and follow-ups so no prospective learner slips away.",
      aiTeaser: "AI will draft follow-up messages and flag enquiries going cold.",
    },
    accounting: {
      blurb:
        "See fee income against every expense — rent, electricity, salaries — for a clear gross and net profit.",
      aiTeaser: "AI will auto-categorise expenses and explain what moved your profit.",
    },
    payroll: {
      blurb: "Pay your team and roll their cost straight into your profit picture.",
      aiTeaser: "AI will flag unusual salary runs before you approve them.",
    },
    audit_export: {
      blurb:
        "Export a tamper-evident record of who did what, for DPDPA and your own peace of mind.",
    },
    dpdp_consent: {
      blurb: "Capture and honour each learner's data-consent under India's DPDP Act.",
    },
    fees: {
      blurb: "Hourly, monthly or per-session plans — collect online and track dues at a glance.",
      aiTeaser: "AI will nudge learners with pending dues at the right time.",
    },
    attendance: {
      blurb: "QR check-in tied to a seat or shift — know who's in the room, live.",
    },
    communications: {
      blurb: "Message learners directly over email, SMS and WhatsApp — no parent middle-man.",
      aiTeaser: "AI will draft announcements in your centre's voice.",
    },
    reports: {
      blurb: "Occupancy, check-ins, revenue and profit — the numbers that run your centre.",
      aiTeaser: "AI will summarise the week and surface what needs attention.",
    },
    helpdesk: {
      blurb: "One inbox for learner questions, with SLAs so nothing is missed.",
    },
  },
};

const SCHOOL: VerticalProfile = {
  vertical: "school",
  institutionNoun: "School",
  terms: {
    learner: { one: "Student", many: "Students" },
    team: { one: "Staff member", many: "Staff" },
    mentor: { one: "Teacher", many: "Teachers" },
    guardian: { one: "Parent", many: "Parents" },
    enrolmentId: "Roll number",
    membershipNo: "Admission number",
    joiningDate: "Admission date",
    admission: { one: "Admission", many: "Admissions" },
    counsellor: { one: "Counsellor", many: "Counsellors" },
    grade: "Grade",
    section: "Section",
  },
  personNoun: { one: "Student", many: "Students" },
  staffNoun: { one: "Teacher", many: "Teachers & staff" },
  has: {
    grades: true,
    sections: true,
    parents: true,
    classTeachers: true,
    subjects: true,
    periods: true,
    exams: true,
    reportCards: true,
    learnerPortal: false,
    admissionAssessments: true,
  },
  chrome: {
    searchPlaceholder: "Search students, classes, fees…",
    navLabels: { payroll: "Staff pay" },
  },
  personColumns: ["name", "grade", "section", "roll", "parent", "status"],
  dashboardKpis: ["students", "staff", "admissions_open", "fees_collected"],
  moduleCopy: {},
};

const COACHING: VerticalProfile = {
  vertical: "coaching_institute",
  institutionNoun: "Coaching institute",
  terms: {
    learner: { one: "Student", many: "Students" },
    team: { one: "Staff member", many: "Staff" },
    mentor: { one: "Faculty member", many: "Faculty" },
    guardian: { one: "Parent", many: "Parents" },
    enrolmentId: "Roll number",
    membershipNo: "Admission number",
    joiningDate: "Admission date",
    admission: { one: "Admission", many: "Admissions" },
    counsellor: { one: "Counsellor", many: "Counsellors" },
    grade: "Class",
    section: "Batch",
  },
  personNoun: { one: "Student", many: "Students" },
  staffNoun: { one: "Faculty", many: "Faculty & staff" },
  has: {
    grades: false,
    sections: false,
    parents: true,
    classTeachers: false,
    subjects: true,
    periods: false,
    exams: true,
    reportCards: false,
    learnerPortal: false,
    admissionAssessments: true,
  },
  chrome: {
    // Coaching runs batches, not classes — and keeps subjects, so the
    // academic catalogue stays.
    searchPlaceholder: "Search students, batches, fees…",
    navLabels: { payroll: "Staff pay" },
  },
  personColumns: ["name", "phone", "parent", "status", "joined"],
  dashboardKpis: ["students", "staff", "admissions_open", "fees_collected"],
  moduleCopy: {},
};

const PROFILES: Record<TenantVerticalLiteral, VerticalProfile> = {
  study_centre: STUDY_CENTRE,
  school: SCHOOL,
  coaching_institute: COACHING,
};

/** Resolve the presentation profile for a vertical. */
export function getVerticalProfile(vertical: TenantVerticalLiteral): VerticalProfile {
  return PROFILES[vertical];
}

/**
 * Centre-facing copy. Internal role and catalog words stay in the API;
 * a study centre never sees "student" or "teacher" on screen.
 */
export function clientFacingText(vertical: TenantVerticalLiteral, text: string): string {
  if (vertical === "coaching_institute") {
    return text
      .replace(/\b(?:A|a) teacher\b/g, (match) => `${match[0]} faculty member`)
      .replace(/\bTeachers\b/g, "Faculty")
      .replace(/\bteachers\b/g, "faculty")
      .replace(/\bTeacher\b/g, "Faculty member")
      .replace(/\bteacher\b/g, "faculty member");
  }
  if (vertical !== "study_centre") return text;
  return text
    .replace(/\bRoll numbers\b/g, "Learner IDs")
    .replace(/\broll numbers\b/g, "learner IDs")
    .replace(/\bRoll (?:number|no\.)|\bRoll #/g, "Learner ID")
    .replace(/\broll (?:number|no\.)|\broll #/g, "learner ID")
    .replace(/\bAdmission (?:number|no\.)|\bAdmission #/g, "Membership no.")
    .replace(/\badmission (?:number|no\.)|\badmission #/g, "membership no.")
    .replace(/\bStaff members\b/g, "Team members")
    .replace(/\bStaff member\b/g, "Team member")
    .replace(/\bstaff members\b/g, "team members")
    .replace(/\bstaff member\b/g, "team member")
    .replace(/\bStaff\b/g, "Team")
    .replace(/\bstaff\b/g, "team")
    .replace(/\bCounsell?ors\b/g, "Team members")
    .replace(/\bCounsell?or\b/g, "Team member")
    .replace(/\bcounsell?ors\b/g, "team members")
    .replace(/\bcounsell?or\b/g, "team member")
    .replace(/\bStudents\b/g, "Learners")
    .replace(/\bstudents\b/g, "learners")
    .replace(/\bStudent\b/g, "Learner")
    .replace(/\bstudent\b/g, "learner")
    .replace(/\bTeachers\b/g, "Mentors")
    .replace(/\bteachers\b/g, "mentors")
    .replace(/\bTeacher\b/g, "Mentor")
    .replace(/\bteacher\b/g, "mentor")
    .replace(/\bParents\b/g, "Guardians")
    .replace(/\bparents\b/g, "guardians")
    .replace(/\bParent\b/g, "Guardian")
    .replace(/\bparent\b/g, "guardian");
}

const STUDY_CENTRE_EXPORT_HEADERS: Record<string, string> = {
  "Staff name": "Team member",
  "Staff email": "Team member email",
};

/**
 * Header text for a downloaded CSV. Column order stays put; only the
 * words change. Study centres say Learner, Guardian and Team member.
 */
export function exportHeader(vertical: TenantVerticalLiteral, header: string): string {
  if (vertical === "study_centre") {
    const exact = STUDY_CENTRE_EXPORT_HEADERS[header];
    if (exact) return exact;
  }
  return clientFacingText(vertical, header);
}

export function roleLabelFor(
  vertical: TenantVerticalLiteral,
  role: string,
  fallback: string,
): string {
  if (vertical === "coaching_institute") return role === "teacher" ? "Faculty" : fallback;
  if (vertical !== "study_centre") return fallback;
  if (role === "teacher") return "Mentor";
  if (role === "staff") return "Team member";
  return fallback;
}

/** Client-facing nouns for a vertical (Phase SC-22). */
export function termsFor(vertical: TenantVerticalLiteral): VerticalTerms {
  return PROFILES[vertical].terms;
}

/**
 * Human label for a permission code. Codes like `view_staff` or
 * `import_students` never reach a client screen.
 */
export function permissionLabelFor(vertical: TenantVerticalLiteral, code: string): string {
  const known = PERMISSION_LABELS[code];
  const sentence = known ?? code.replace(/_/g, " ").replace(/^./, (first) => first.toUpperCase());
  return clientFacingText(vertical, sentence);
}

const PERMISSION_LABELS: Record<string, string> = {
  view_students: "View students",
  manage_students: "Manage students",
  import_students: "Import students",
  view_staff: "View staff",
  manage_staff: "Manage staff",
  invite_staff: "Invite staff",
  view_parents: "View parents",
  manage_parents: "Manage parents",
  view_admissions: "View enquiries and admissions",
  manage_admissions: "Manage enquiries and admissions",
  view_fees: "View fees",
  manage_fees: "Manage fees",
  manage_forms: "Manage forms",
  view_forms: "View forms",
  manage_settings: "Manage settings",
  view_reports: "View reports",
  manage_designations: "Manage roles and access",
};

/**
 * Convenience for copy: the person noun for a vertical, singular when
 * `count === 1`, plural otherwise (and plural when count is omitted).
 */
export function personNounFor(vertical: TenantVerticalLiteral, count?: number): string {
  const noun = PROFILES[vertical].personNoun;
  return count === 1 ? noun.one : noun.many;
}

/**
 * Sidebar label for a module row. Falls back to the catalog's neutral
 * label, so a vertical only overrides what it actually renames.
 */
export function moduleNavLabelFor(profile: VerticalProfile, key: string, fallback: string): string {
  return profile.chrome.navLabels[key] ?? fallback;
}

/**
 * Label for the standing bulk-importer nav row and page. Derived from
 * the person noun rather than declared per vertical so a vertical can
 * never rename its people and forget its importer.
 */
export function importPeopleLabelFor(profile: VerticalProfile): string {
  return `Import ${profile.personNoun.many.toLowerCase()}`;
}

/**
 * Whether the academic catalogue (subjects, sections, and the
 * subject×grade×section coverage matrix) means anything here.
 *
 * A study centre has none of those three concepts — learners bring
 * their own material and sit at a seat — so the whole Catalogue
 * surface, and the readiness gates that hang off it ("No sections
 * configured", "No class teachers assigned"), are school scaffolding
 * that a centre owner can only be confused by. Coaching keeps
 * subjects, so it keeps the catalogue.
 */
export function hasAcademicCatalogue(profile: VerticalProfile): boolean {
  return profile.has.subjects || profile.has.sections;
}

/** Whether the H5 Assessments authoring workspace belongs in IA nav. */
export function hasAdmissionAssessments(profile: VerticalProfile): boolean {
  return profile.has.admissionAssessments;
}
